/**
 * ============================================================================
 * useOosDetailQuery — Phase 7 frontend hook.
 * Owner: Phase 7 — Out-of-Specification Module.
 *
 * Wraps GET /api/v1/oos/{id} and returns the full payload + the appended
 * history timeline. Used by the OosInvestigation screen for Phase 1 + Phase 2
 * + close decisions.
 * ============================================================================ */

import { useCallback, useEffect, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import type { OosStatusValue, OosKindValue } from '../OosLog/useOosListQuery';

export type Phase1Outcome = 'ASSIGNABLE_CAUSE_FOUND' | 'NEGATIVE';

export type AssignableCauseKind =
  | 'LABORATORY_ERROR'
  | 'MANUFACTURING_ASSIGNABLE'
  | 'INHERENT_VARIABILITY'
  | 'OTHER';

export interface OosHistoryItem {
  historyId: string;
  eventCode: string;
  eventAtUtc: string;
  actorUserId: string | null;
  payload: Record<string, unknown>;
}

export interface OosDetail {
  id: string;
  tenantId: string;
  oosNumber: string;
  sampleRef: string;
  batchRef: string | null;
  testMethodRef: string;
  specLowerBound: string | null;
  specUpperBound: string | null;
  specUnits: string;
  resultValue: string;
  resultUnits: string;
  outOfSpecKind: OosKindValue;
  status: OosStatusValue;
  phase2Required: boolean;
  reportedAtUtc: string;
  reporterUserId: string;
  phase1StartedUtc: string | null;
  phase1ConcludedUtc: string | null;
  phase1Outcome: Phase1Outcome | null;
  phase1Summary: string | null;
  phase1SignerUserId: string | null;
  phase2StartedUtc: string | null;
  phase2ConcludedUtc: string | null;
  assignableCauseKind: AssignableCauseKind | null;
  finalConclusion: string | null;
  closedAtUtc: string | null;
  closureSignerUserId: string | null;
  createsDeviation: boolean;
  deviationId: string | null;
  createsCapa: boolean;
  capaId: string | null;
  history: OosHistoryItem[];
  // C-6 — FDA Phase-1 lab checklist + retest/resample tracking.
  phase1Checklist?: Phase1ChecklistItem[];
  retests?: OosRetest[];
}
export interface Phase1ChecklistItem { hypothesis: string; ruledOut: boolean; note: string | null }
export interface OosRetest {
  retestId: string;
  kind: 'RETEST' | 'RESAMPLE';
  resultValue: number | null;
  resultUnits: string | null;
  outcome: 'WITHIN_SPEC' | 'OUT_OF_SPEC';
  notes: string | null;
  performedAtUtc: string;
  performedByName: string | null;
}

interface QueryResult {
  data?: OosDetail;
  isLoading: boolean;
  isError: boolean;
  refetch?: () => Promise<void>;
}

export function useOosDetailQuery(oosId: string): QueryResult {
  const [data, setData] = useState<OosDetail | undefined>(undefined);
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const resp = await fetch(apiUrl(`/api/v1/oos/${encodeURIComponent(oosId)}`), {
        headers: { ...buildHeaders(), Accept: 'application/json' },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = (await resp.json()) as OosDetail;
      setData(json);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [oosId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  return { data, isLoading, isError, refetch: fetchDetail };
}
